import { z } from 'zod';

export const roles = ['USER', 'REVIEWER', 'ADMIN'] as const;
export type Role = (typeof roles)[number];
export const chordStatuses = [
  'DRAFT',
  'PENDING_REVIEW',
  'CHANGES_REQUESTED',
  'APPROVED',
  'REJECTED',
  'ARCHIVED',
] as const;
export type ChordStatus = (typeof chordStatuses)[number];
export const reviewDecisions = ['APPROVED', 'REJECTED', 'CHANGES_REQUESTED'] as const;

export const chordSheetInput = z.object({
  title: z.string().trim().min(1).max(160),
  artistName: z.string().trim().min(1).max(120),
  genreIds: z.array(z.string().min(1)).min(1),
  originalKey: z.string().trim().max(8),
  displayedKey: z.string().trim().max(8).optional(),
  capo: z.number().int().min(0).max(12).default(0),
  tuning: z.string().trim().min(1).max(50),
  timeSignature: z.string().trim().max(12).optional(),
  difficulty: z.enum(['BEGINNER', 'INTERMEDIATE', 'ADVANCED']).default('INTERMEDIATE'),
  language: z.string().trim().max(40).optional(),
  youtubeUrl: z.string().url().max(300).optional().or(z.literal('')),
  strummingPattern: z.string().trim().min(1).max(500),
  content: z.string().trim().min(1).max(100_000),
  notes: z.string().trim().max(3000).optional(),
});
export type ChordSheetInput = z.infer<typeof chordSheetInput>;

export const slugify = (value: string): string =>
  value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
export const normalizeName = (value: string): string =>
  value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('en-US');

export const canReview = (role: Role): boolean => role === 'REVIEWER' || role === 'ADMIN';
export const canAdmin = (role: Role): boolean => role === 'ADMIN';
export const promotionEligible = (role: Role, status: string, approvedCount: number): boolean =>
  role === 'USER' && status === 'ACTIVE' && approvedCount >= 100;

const youtubeHosts = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be']);
export function youtubeVideoId(value?: string): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (!youtubeHosts.has(url.hostname)) return null;
    const id = url.hostname === 'youtu.be' ? url.pathname.slice(1) : url.searchParams.get('v');
    return id && /^[\w-]{11}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}
